import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  Alert,
} from "reactstrap";
import classnames from "classnames";
import { Link } from "react-router-dom";

// components
import LightBox from "../../../components/LightBox";
import RepliedMessage from "./RepliedMessage";
import LocationMessage from "./LocationMessage";

import { extractUrl } from "../../../utils/arrayutils";
import { useLinkPreview } from "../../../hooks/useLinkPreview";
import { LinkPreviewCard } from "./LinkPreviewCard";

// images
import imageContact from "../../../assets/images/users/user-dummy-img.jpg"; //Usuario
import imageProfile from "../../../assets/images/users/avatar-1.jpg"; //Voce 

// utils
import { formatMessageDate } from "../../../utils";
import { IAttachmentTypes, IImageTypes, IMessage, IMessagesTypes, mapAttachmentToTypes, parseLocation } from "../../../types/message";
import { IContactDetailsTypes, IProfileDetailsTypes } from "../../../types/profile";
import ContactSharedMessage from "./ContactSharedMessage";

// emoji picker
import { useMessages } from "../../../context/MessagesContext";
import { useAuth } from "../../../context/AuthContext";
import { useProfileImage } from "../../../context/ProfileImageContext";



const imgStyle: React.CSSProperties = {
  width: "100%",
  height: "auto",
  borderRadius: 8,
  display: "block",
  transition: "filter 300ms ease, opacity 300ms ease",
};

const barsStyle: React.CSSProperties = {
  display: "inline-flex",
  gap: 4,
  alignItems: "end",
  marginRight: 8,
  verticalAlign: "middle",
};

const barBase: React.CSSProperties = {
  width: 3,
  background: "rgba(0,0,0,0.15)",
  borderRadius: 2,
  transition: "height 160ms linear",
};

interface MessageStickerProps {
  src: string; // URL, data:image/*;base64,... ou object URL
  alt?: string;
  size?: number; // tamanho em px (quadrado)
  className?: string;
  onClick?: () => void;
  lazy?: boolean;
}

/* ========================== */
/* COMPONENTES AUXILIARES     */
/* ========================== */

// helpers de ícone por mime
const getFileIconFromMime = (name: string) => {
  // console.log('getFileIconFromMime', name);
  if (!name) return "bxs-file";

  // Normaliza para comparação
  const ext = name.split(".").pop()?.toLowerCase();
  const m = (ext as string).toLowerCase();

  if (m.includes("pdf")) return "bxs-file-pdf";

  if (m.includes("video") || m.includes("mp4")) return "bxs-video";

  if (m.includes("audio") || (m.includes("mp3") || m.includes("ogg"))) return "bxs-music";

  if (m.includes("zip") || m.includes("rar") || m.includes("archive"))
    return "bxs-file-archive";

  if (m.includes("word") || m.includes("doc") || m.includes("docx"))
    return "bxs-file-doc";

  if (m.includes("excel") || m.includes("xls") || m.includes("xlsx") || m.includes("spreadsheet") || m.includes("sheet"))
    return "bxs-file";

  if (m.includes("text")) return "bxs-file-txt";

  if (m.includes("image") || m.includes("jpg") || m.includes("png")) return "bxs-image"; // se quiser mostrar ícone

  return "bxs-file"; // fallback
};


export const ImageWithBlur: React.FC<{ src: string; alt?: string; className?: string }> = ({ src, alt, className }) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <img
      src={src}
      alt={alt || ""}
      loading="lazy"
      className={className}
      style={{
        ...imgStyle,
        // width: "100%", display: "block",
        filter: loaded ? "none" : "blur(12px) grayscale(10%)",
        opacity: loaded ? 1 : 0.95,
      }}
      onLoad={() => setLoaded(true)}
    />
  );
};

export const PDFPreview: React.FC<{ url: string; name?: string }> = ({ url, name }) => {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ marginTop: 8 }}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="btn btn-sm btn-light mb-2"
        aria-expanded={open}
      >
        {open ? "Fechar preview" : "Visualizar PDF"}
      </button>

      {open && (
        <div style={{ width: "100%", height: 420, borderRadius: 8, overflow: "hidden", boxShadow: "0 8px 24px rgba(0,0,0,0.08)" }}>
          <iframe
            src={url}
            title={name || "PDF Preview"}
            style={{ width: "100%", height: "100%", border: "none" }}
          />
        </div>
      )}
    </div>
  );
};

export const VideoThumb: React.FC<{ url: string; poster?: string }> = ({ url, poster }) => {
  const ref = React.useRef<HTMLVideoElement | null>(null);
  const [playing, setPlaying] = React.useState(false);

  const togglePlay = () => {
    if (!ref.current) return;

    if (ref.current.paused) {
      ref.current.play();
      setPlaying(true);
    } else {
      ref.current.pause();
      setPlaying(false);
    }
  };

  return (
    <div
      style={{
        position: "relative",
        borderRadius: 8,
        overflow: "hidden",
        width: "100%",      // ocupa a largura total permitida
        maxWidth: 260,      // limite visual estilo WhatsApp
        cursor: "pointer",
      }}
      onClick={togglePlay}
    >
      <video
        ref={ref}
        src={url}
        poster={poster}
        preload="metadata"
        style={{
          width: "100%",     // vídeo ocupa toda a largura
          height: "auto",    // mantém proporção original
          display: "block",
          objectFit: "cover",
        }}
        onEnded={() => setPlaying(false)}
        controls={false}
      />

      {/* <h1>AQUI ESTA SEU VIDEO</h1> */}

      {!playing && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.25)",
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.9)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 26,
              fontWeight: "bold",
              color: "#333",

            }}
          >
            ▶
          </div>
        </div>
      )}
    </div>
  );
};

export const AudioWave: React.FC<{ url: string }> = ({ url }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState("0:00");
  const [current, setCurrent] = useState("0:00");

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;

    a.onloadedmetadata = () => {
      setDuration(format(a.duration));
    };

    a.ontimeupdate = () => {
      setCurrent(format(a.currentTime));
    };
  }, []);

  const toggle = async () => {
    if (!audioRef.current) return;

    if (audioRef.current.paused) {
      await audioRef.current.play();
      setPlaying(true);
    } else {
      audioRef.current.pause();
      setPlaying(false);
    }
  };

  const format = (sec: number) => {
    if (!sec) return "0:00";
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60)
      .toString()
      .padStart(2, "0");
    return `${m}:${s}`;
  };

  return (
    <div style={styles.container}>
      {/* Botão Play/Pause */}
      <button style={styles.playButton} onClick={toggle}>
        {playing ? (
          <i className="bx bx-pause" style={styles.icon}></i>
        ) : (
          <i className="bx bx-play" style={styles.icon}></i>
        )}
      </button>

      {/* Waveform */}
      <div style={styles.waveContainer}>
        {Array.from({ length: 22 }).map((_, i) => (
          <div
            key={i}
            style={{
              ...styles.bar,
              height: playing
                ? 6 + Math.sin(i + Date.now() / 100) * 6
                : 4 + (i % 4) * 3,
              opacity: playing ? 0.9 : 0.3,
            }}
          ></div>
        ))}
      </div>

      {/* Tempo */}
      <div style={styles.time}>{playing ? current : duration}</div>

      <audio
        ref={audioRef}
        src={url}
        onEnded={() => {
          setPlaying(false);
          setCurrent("0:00");
        }}
        preload="none"
      />
    </div>
  );
};

export const MessageSticker: React.FC<MessageStickerProps> = ({
  src,
  alt = "sticker",
  size = 140,
  className,
  onClick,
  lazy = true,
}) => {
  // estilo inline para garantir comportamento consistente
  const style: React.CSSProperties = {
    width: size,
    height: size,
    objectFit: "contain",
    borderRadius: 12,
    cursor: onClick ? "pointer" : "default",
    display: "block",
  };

  return (
    <>
      <h1> componente Sticker</h1>
      <img
        src={src}
        alt={alt}
        className={className}
        style={style}
        onClick={onClick}
        loading={lazy ? "lazy" : undefined}
        // evita que imagens grandes quebrem o layout
        decoding="async"
        draggable={false}
      />
    </>

  );
};

const Menu = ({
  onMessage,
  onFromMe,
  onDelete,
  onReply,
  onForward,
  onReact,
  openEmojiPicker,
  onSetImgAttachment,
}: {
  onMessage: IMessagesTypes;
  onFromMe: boolean;
  onDelete: () => any;
  onReply: () => any;
  onForward: () => void;
  onReact: (emoji: string) => void;
  openEmojiPicker: () => void;
  onSetImgAttachment: (messageId: string | number) => void;
}) => {

  const handleReaction = (emoji: string) => {
    onReact(emoji)
  };

  return (
    <UncontrolledDropdown className="align-self-start message-box-drop">
      <DropdownToggle className="btn btn-toggle" role="button" tag={"a"}>
        <i className="ri-more-2-fill"></i>
      </DropdownToggle>

      <DropdownMenu>

        {/* ⭐ REAÇÕES ESTILO WHATSAPP */}
        {!onFromMe && (
          <DropdownItem className="py-2">

            <div
              style={{
                display: "flex",
                gap: 8,
                fontSize: 20,
                padding: "4px 6px",
                alignItems: "center",
              }}
            >

              {/* <ReactionBar onSelect={onReact} /> */}
              {/* Pode vir do BD mais usados   */}
              {["🙂", "👍", "❤️", "😂", "😮", "😢"].map((e) => (
                <span
                  key={e}
                  style={{
                    fontSize: "20px",
                    cursor: "pointer",
                    transition: "transform 0.1s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.3)")}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                  onClick={() => handleReaction(e)}
                // onClick={() => onReact(e)}
                >
                  {e}
                </span>
              ))}

              {/* ➕ Botão que abre o emoji picker */}
              <span

                style={{
                  fontSize: "20px",
                  cursor: "pointer",
                  fontWeight: "bold",
                  transition: "transform 0.1s",
                  paddingLeft: 10,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.3)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}

                onClick={() => handleReaction}
              >
                ➕
              </span>
            </div>
          </DropdownItem>
        )}

        <DropdownItem>Copiar <i className="bx bx-copy text-muted ms-2"></i></DropdownItem>
        <DropdownItem>Favoritos <i className="bx bx-bookmarks text-muted ms-2"></i></DropdownItem>
        <DropdownItem onClick={onForward}>
          Encaminhar <i className="bx bx-share-alt ms-2 text-muted"></i>
        </DropdownItem>

        {!onFromMe && (
          <>
            <DropdownItem onClick={onReply}>
              Responder <i className="bx bx-share ms-2 text-muted"></i>
            </DropdownItem>
            <DropdownItem>
              Marcar como não lido <i className="bx bx-message-error text-muted ms-2"></i>
            </DropdownItem>
          </>
        )}

        <DropdownItem onClick={onDelete}>
          Excluir <i className="bx bx-trash text-muted ms-2"></i>
        </DropdownItem>

        {onMessage && (onMessage.type == "image" ||
          onMessage.type == "audio" ||
          onMessage.type == "document") &&

          <DropdownItem onClick={() => onSetImgAttachment(onMessage.mId)}>
            Marcar como Anexo <i className="bx bxs-file-plus ms-2 text-muted"></i>
          </DropdownItem>
        }
      </DropdownMenu>

    </UncontrolledDropdown>
  );
};

const ImageMoreMenu = ({
  idMessage,
  imagelink,
  onReply,
  onDelete,
  onSetImgSelf,
  onSetImgDocument,
  onSetImgResidence,
  onSetImgAttachment,
}: {
  idMessage: number;
  imagelink: string;
  onReply: () => any;
  onDelete: (idMessage: number) => void;

  onSetImgSelf: (messageId: string | number) => void;
  onSetImgDocument: (messageId: string | number) => void;
  onSetImgResidence: (messageId: string | number) => void;
  onSetImgAttachment: (messageId: string | number) => void;

}) => (
  <div className="message-img-link">
    <ul className="list-inline mb-0">
      <UncontrolledDropdown tag="li" color="none" className="list-inline-item dropdown">
        <DropdownToggle tag="a" role="button" className="btn btn-toggle">
          <i className="bx bx-dots-horizontal-rounded"></i>
        </DropdownToggle>
        <DropdownMenu>
          {/* <DropdownItem href={imagelink}  download>
            Download <i className="bx bx-download ms-2 text-muted"></i>
          </DropdownItem> */}
          <DropdownItem as="a" href={imagelink} target="_blank" rel="noopener noreferrer">
            Download <i className="bx bx-link-external ms-2 text-muted"></i>
          </DropdownItem>
          <DropdownItem onClick={onReply}>
            Responder <i className="bx bx-share ms-2 text-muted"></i>
          </DropdownItem>
          {/* <DropdownItem>
            Avançar <i className="bx bx-share-alt ms-2 text-muted"></i>
          </DropdownItem> */}
          {/* <DropdownItem>Favoritos <i className="bx bx-bookmarks text-muted ms-2"></i></DropdownItem> */}
          {/* <DropdownItem onClick={() => onDelete(idMessage)}>
            Marcar como documento Self <i className="bx bx-trash ms-2 text-muted"></i>
          </DropdownItem> */}

          <DropdownItem onClick={() => onSetImgSelf(idMessage)}>
            Marcar como Self <i className="bx bx-user-pin ms-2 text-muted"></i>
          </DropdownItem>

          <DropdownItem onClick={() => onSetImgDocument(idMessage)}>
            Marcar como documento <i className="bx bxs-file-png ms-2 text-muted"></i>
          </DropdownItem>

          <DropdownItem onClick={() => onSetImgResidence(idMessage)}>
            Marcar como Comprovante <i className="bx bxs-map-pin ms-2 text-muted"></i>
          </DropdownItem>

          <DropdownItem onClick={() => onSetImgAttachment(idMessage)}>
            Marcar como Anexo <i className="bx bxs-file-plus ms-2 text-muted"></i>
          </DropdownItem>

        </DropdownMenu>
      </UncontrolledDropdown>
    </ul>
  </div>
);

/* ========================== */
/* COMPONENTES DE IMAGEM E ANEXOS */
/* ========================== */

const Image = ({
  message,
  image,
  onImageClick,
  index,
  onSetReplyData,
  onDeleteImg,

  onSetImgSelf,
  onSetImgDocument,
  onSetImgResidence,
  onSetImgAttachment,


}: {
  message: IMessagesTypes;
  image: IImageTypes;
  onImageClick: (id: number) => void;
  index: number;
  onSetReplyData: (reply: null | IMessagesTypes) => void;
  onDeleteImg: (imageId: string | number) => void;

  onSetImgSelf: (messageId: string | number) => void;
  onSetImgDocument: (messageId: string | number) => void;
  onSetImgResidence: (messageId: string | number) => void;
  onSetImgAttachment: (messageId: string | number) => void;

}) => {
  const onDelete = () => onDeleteImg(image.id);
  const onClickReply = () => {
    const selectedImage = (message?.image || []).filter((img) => img.id === image.id);
    onSetReplyData({ ...message, newimage: selectedImage });
  };

  const [loaded, setLoaded] = useState(false);
  return (
    <div className="message-img-list">
      <div>
        <Link className="popup-img d-inline-block w-100" to="#" onClick={() => onImageClick(index)}>
          <img src={image.downloadLink || imageContact} alt=""
            // className="rounded border"
            className=""
            loading="lazy"
            style={{
              ...imgStyle,
              // width: "100%", display: "block",
              filter: loaded ? "none" : "blur(12px) grayscale(10%)",
              opacity: loaded ? 1 : 0.95,
            }}
            onLoad={() => setLoaded(true)}
          />
        </Link>
      </div>
      <ImageMoreMenu
        idMessage={message.mId}
        imagelink={image.downloadLink}
        onReply={onClickReply}
        onDelete={onDelete}

        onSetImgSelf={onSetImgSelf}
        onSetImgDocument={onSetImgDocument}
        onSetImgResidence={onSetImgResidence}
        onSetImgAttachment={onSetImgAttachment}

      />
    </div>
  );
};

const Images = ({
  message,
  images,
  onSetReplyData,
  onDeleteImg,
  onSetImgSelf,
  onSetImgDocument,
  onSetImgResidence,
  onSetImgAttachment,
}: {
  message: IMessagesTypes;
  images: IImageTypes[];
  onSetReplyData: (reply: null | IMessagesTypes) => void;
  onDeleteImg: (imageId: string | number) => void;
  onSetImgSelf: (messageId: string | number) => void;
  onSetImgDocument: (messageId: string | number) => void;
  onSetImgResidence: (messageId: string | number) => void;
  onSetImgAttachment: (messageId: string | number) => void;

}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(0);
  const onImageClick = (id: number) => {

    setSelected(id);
    setIsOpen(true);
  };
  const onClose = () => setIsOpen(false);

  return (
    <>
      <div className="message-img mb-0 ">
        {(images || []).map((image, key) => (
          <Image
            key={key}
            message={message}
            image={image}
            index={key}
            onImageClick={onImageClick}
            onSetReplyData={onSetReplyData}
            onDeleteImg={onDeleteImg}

            onSetImgSelf={onSetImgSelf}
            onSetImgDocument={onSetImgDocument}
            onSetImgResidence={onSetImgResidence}
            onSetImgAttachment={onSetImgAttachment}
          />
        ))}
      </div>
      {isOpen && (
        <LightBox isOpen={isOpen} images={images} onClose={onClose} defaultIdx={selected} />
      )}
    </>
  );
};

const AttachmentRow: React.FC<{ attachment: IAttachmentTypes }> = ({ attachment }) => {
  const mime = (attachment as any).mimeType || ""; // se não tiver, backend pode adicionar
  const icon = getFileIconFromMime(attachment.name);

  // Detect preview possibilities

  const isImage = mime.startsWith("image") || /\.(jpg|jpeg|png|gif|webp|dat)$/i.test(attachment.name);
  // const isSticker = mime.startsWith("image") || /\.(jpg|jpeg|png|webp|gif|dat)$/i.test(attachment.name);
  const isPdf = mime === "application/pdf" || /\.pdf$/i.test(attachment.name);

  return (
    <div
      className={classnames("p-3 border-primary border rounded-3 d-flex align-items-start", {
        "mt-2": true,
      })}
      style={{ gap: 12 }}
    >
      <div className="flex-shrink-0 me-3" style={{ minWidth: 48 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 8, display: "flex",
          alignItems: "center", justifyContent: "center",
          background: "#f5f7fa"
        }}>
          <i className={`bx ${icon} font-size-20`} />
        </div>
      </div>

      <div style={{ flex: 1 }}>
        {/* <h5 className="font-size-14 mb-1 text-break">{attachment.desc}</h5> */}
        <h5 className="font-size-14 mb-1 text-break">{attachment.name}</h5>
        <p className="text-muted font-size-13 mb-2 text-break">{attachment.desc}</p>

        {/* Preview area */}
        {isImage && (
          <>
            <div style={{ maxWidth: 780 }}>
              <ImageWithBlur src={attachment.downloadLink} alt={attachment.name} />
            </div>
          </>
        )}

        {/* <MessageSticker size={160} src={attachment.downloadLink} alt={attachment.name} /> */}


        {isPdf && (
          <PDFPreview url={attachment.downloadLink} name={attachment.name} />
        )}

      </div>

    </div>
  );
};

export const Attachments = ({ attachments }: { attachments?: IAttachmentTypes[] }) => {
  if (!attachments?.length) return null;

  const filtered = attachments.filter(att =>
    !att.mimeType?.toLowerCase().startsWith("audio") &&
    !/\.(mp3|wav|ogg)$/i.test(att.name || "")
    &&
    !att.mimeType?.toLowerCase().startsWith("video") &&
    !/\.(mp4|mov|webm)$/i.test(att.name || "")
  );

  if (!filtered.length) return null;

  return (
    <div>
      {filtered.map(att => (
        <AttachmentRow key={att.id} attachment={att} />
      ))}
    </div>
  );
};


/* ========================== */
/* COMPONENTE PRINCIPAL (Message) */
/* ========================== */

const Message = ({
  message,
  chatUserDetails,

  onSetImgSelf,
  onSetImgDocument,
  onSetImgResidence,
  onSetImgAttachment,

  onDelete,
  onSetReplyData,
  isFromMe,
  onOpenForward,
  isChannel,
  onDeleteImage,
}: {
  message: IMessagesTypes;
  chatUserDetails?: IContactDetailsTypes | null;
  onSetImgSelf: (messageId: string | number) => void;
  onSetImgDocument: (messageId: string | number) => void;
  onSetImgResidence: (messageId: string | number) => void;
  onSetImgAttachment: (messageId: string | number) => void;

  onDelete: (messageId: string | number) => any;
  onSetReplyData: (reply: null | IMessagesTypes) => void;
  isFromMe: boolean;
  onOpenForward: (message: IMessagesTypes) => void;
  isChannel: boolean;
  onDeleteImage: (messageId: string | number, imageId: string | number) => void;
}) => {

  // ✅ fallback seguro para evitar undefined
  const { getAttachmentsForMessage, sendReactionMessage } = useMessages();
  const safeUser = chatUserDetails;
  const { user } = useAuth();
  const [reactionPickerOpen, setReactionPickerOpen] = useState(false);

  const { avatarImage } = useProfileImage();
  const avatarProfile = avatarImage || imageProfile;

  const onEmojiMessage = async (emoji: string) => {
    // console.log("onEmojiMessage", emoji);
    setReactionPickerOpen(false);

    if (!safeUser?.basicDetails.id) return;

    await sendReactionMessage({
      'idUser': user?.id, 'idContact': safeUser?.basicDetails.id,
      'idMessage': message.mId, 'reaction': emoji
    });
  };

  // ✅ Proteções contra dados indefinidos
  const hasImages = Array.isArray(message?.image) && message.image.length > 0;
  const hasAttachments = Array.isArray(message?.attachments) && message.attachments.length > 0;
  const hasText = !!message?.text;
  const isContaction = message?.type === "contacts";
  const isLocation = message?.type === "location";
  const isSessionClosed = message?.type === "session";
  const isTemplate = message?.type === "template";

  const isAudio =
    message?.attachments &&
    message.attachments.length > 0 &&
    (
      message.attachments[0].mimeType?.startsWith("audio") ||
      /\.(mp3|ogg|wav)$/i.test(message.attachments[0].name || "")
    );

  const isVideo =
    message?.attachments &&
    message.attachments.length > 0 &&
    (
      message.attachments[0].mimeType?.startsWith("video/mp4") ||
      /\.(mp4|mov|webm)$/i.test(message.attachments[0].name || "")
    );

  const isSticker =
    message?.attachments &&
    message.attachments.length > 0 &&
    (
      message.attachments[0].mimeType?.startsWith("image") ||
      /\.(webm)$/i.test(message.attachments[0].name || "")
    );

  let location = {
    text: "" as string,
    url: "" as string,
    latitude: null as number | null,
    longitude: null as number | null,
  };

  if (isLocation) {
    location = parseLocation(message?.text as string);
  }

  const chatUserFullName = chatUserDetails
    ? `${chatUserDetails?.basicDetails.firstName}`.trim() || "-"
    : "Desconhecido";

  isFromMe = message.meta.sender === 1 ? false : true;

  const dateMessage = formatMessageDate(message?.time);

  const channelSenderFullName = message?.meta?.userData
    ? `${message.meta.userData.name}`.trim()
    : "- Desconhecido";


  const fullName = chatUserFullName;
  const onDeleteMessage = () => onDelete(message?.mId);
  const onClickReply = () => onSetReplyData(message);
  const onForwardMessage = () => onOpenForward(message);
  const onDeleteImg = (imageId: number | string) => onDeleteImage(message?.mId, imageId);


  const {
    sent: isSent,
    received: isReceived,
    read: isRead,
    isForwarded,
  } = message.meta;

  // Detectar URL no texto
  const urlInText = message?.text ? extractUrl(message.text) : "";

  // Buscar preview automaticamente
  let preview = "";
  preview = useLinkPreview(urlInText as string);

  const dataHoraPorExtenso = (date: Date) => {
    const meses = [
      "janeiro", "fevereiro", "março", "abril", "maio", "junho",
      "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"
    ];

    const h = date.getHours();
    const m = date.getMinutes();
    const hora = m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, "0")}`;

    return `${date.getDate()} de ${meses[date.getMonth()]} de ${date.getFullYear()} às ${hora}`;
  };

  return (
    <li
      className={classnames(
        "chat-list",
        "w-100",              // ⬅ força a largura total
        { right: isFromMe },
        { reply: message?.replyOf }
      )}
    >
      <div className="conversation-list">
        <div className="chat-avatar">
          <img src={isFromMe ? avatarProfile : imageContact} alt="" />
        </div>

        <div className="user-chat-content">
          {hasImages && message?.text && (
            <div className="ctext-wrap">
              <div className="ctext-wrap-content">
                <p className="mb-0 ctext-content message-text">
                  {message.text}
                </p>
              </div>
            </div>
          )}

          {isForwarded && (
            <span className="text-muted font-size-13 mb-1 d-block">
              <i className="ri ri-share-forward-line align-middle me-1"></i>
              Encaminhado
            </span>
          )}

          <div className="ctext-wrap">
            {hasImages ? (
              <>
                <Images
                  message={message}
                  images={message.image!}
                  onSetReplyData={onSetReplyData}
                  onDeleteImg={onDeleteImg}
                  onSetImgSelf={onSetImgSelf}
                  onSetImgDocument={onSetImgDocument}
                  onSetImgResidence={onSetImgResidence}
                  onSetImgAttachment={onSetImgAttachment}
                />
              </>


            ) : (
              <>
                <div className="w-100 ctext-wrap-content">
                  {message?.replyOf && (
                    <RepliedMessage fullName={fullName as string} message={message} isFromMe={isFromMe} />
                  )}

                  {hasText &&
                    (
                      <>

                        {/* Texto da mensagem e se for link nao mostra */}
                        {!preview && (message.type === "text" || message.type === "button" ||
                          message.type === "session" || message.type === "template")
                          && typeof message.text === "string" &&
                          message.text.trim().length > 0 && (
                            <>
                              {isSessionClosed ? (
                                <span className="text-danger font-bold font-size-13 mb-1 d-block message-text">
                                  <i className="ri ri-close-circle-line align-middle me-1"></i>
                                  Atendimento Encerrado em {dataHoraPorExtenso(new Date(message.time))}
                                </span>
                              ) :

                                <p key={`${message.mId}-${message.reaction}`}
                                  className="mb-0 ctext-content message-text">
                                  {isTemplate ? (
                                    <div className="tab-list-link">
                                      📄 Template enviado
                                      <br />
                                      👍 Vamos aguardar o retorno do cliente
                                    </div>
                                  ) :
                                    (<>{message.text}</>)
                                  }

                                </p>
                              }

                            </>

                          )}



                        {/* Preview automático quando existir link */}
                        {!isLocation && preview && <LinkPreviewCard preview={preview} />}

                        {/* Reação */}
                        {message.reaction && (
                          <span className="reaction">
                            {message.reaction}
                          </span>
                        )}

                        {/* Location */}
                        {isLocation && (
                          <LocationMessage
                            text={location.text}
                            url={location.url}
                            latitude={location.latitude}
                            longitude={location.longitude}
                          />
                        )}

                        {/* isContaction */}
                        {isContaction && (
                          <ContactSharedMessage contactId={message.text as string}
                          />
                        )}

                        {isAudio && (
                          message?.attachments?.[0]?.downloadLink ? (
                            <AudioWave
                              url={message.attachments[0].downloadLink}
                            />
                          ) : (
                            <div className="audio-placeholder">
                              🎧 Carregando áudio…
                            </div>
                          )
                        )}

                        {isVideo && message?.attachments?.[0]?.downloadLink && (
                          <VideoThumb url={message.attachments[0].downloadLink} />
                        )}

                        {isSticker && message?.attachments?.[0]?.downloadLink && (
                          <div style={{ maxWidth: 780 }}>
                            <MessageSticker size={160} src={message.attachments[0].downloadLink} alt={message.attachments[0].name} onClick={() => null} />
                          </div>
                        )}

                      </>

                    )

                  }

                  {hasAttachments && <Attachments attachments={message.attachments} />}
                </div>


                <Menu
                  onFromMe={isFromMe}
                  onForward={onForwardMessage}
                  onDelete={onDeleteMessage}
                  onReply={onClickReply}
                  onReact={onEmojiMessage}
                  openEmojiPicker={() => setReactionPickerOpen(!reactionPickerOpen)}

                  onMessage={message}
                  onSetImgAttachment={onSetImgAttachment}
                />

              </>
            )}
          </div>

          <div className="conversation-name">
            {isFromMe ? (
              <>
                <span
                  className={classnames("me-1", {
                    "text-success": isRead,
                    "text-muted": !isRead,
                  })}
                >
                  <i
                    className={classnames("bx", {
                      "bx-check-double": isRead || isReceived,
                      "bx-check": isSent && !isReceived && !isRead,
                    })}
                  />
                </span>

                <small className="text-muted mb-0 me-2">{dateMessage}</small>
                Você
              </>
            ) : (
              <>
                {fullName}
                <small className="text-muted mb-0 ms-2">{dateMessage}</small>
              </>
            )}
          </div>
        </div>
      </div>
    </li>
  );
};

export default Message;

/* ------------------- STYLES ------------------- */
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    background: "#f5f7fb",
    padding: "10px 14px",
    borderRadius: 14,
    maxWidth: 320,
    border: "1px solid #ececec",
  },
  playButton: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    border: "none",
    background: "#0a84ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#fff",
    boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
  },
  icon: {
    fontSize: 20,
  },
  waveContainer: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    flexGrow: 1,
    height: 24,
  },
  bar: {
    width: 3,
    background: "#0a84ff",
    borderRadius: 3,
    transition: "height 0.15s ease-out",
  },
  time: {
    fontSize: 12,
    color: "#666",
    width: 40,
    textAlign: "right",
  },
};